import { useEffect, useState } from "react"
import { listWorkers, registerWorker } from "@/services/api"
import type { Worker } from "@/types"
import { Plus } from "lucide-react"
import { Panel, Stat, StatGrid } from "@/components/ui"

export default function WorkersPage() {
  const [workers, setWorkers] = useState<Worker[]>([])
  const [loading, setLoading] = useState(true)
  const [registering, setRegistering] = useState(false)
  const [form, setForm] = useState({
    name: "",
    hostname: typeof window !== "undefined" ? window.location.hostname : "",
    cpu_info: "",
    token: "",
  })

  useEffect(() => {
    load()
  }, [])

  async function load() {
    const res = await listWorkers()
    setWorkers(res.data)
    setLoading(false)
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    setRegistering(true)
    try {
      await registerWorker(form)
      setForm({
        name: "",
        hostname: typeof window !== "undefined" ? window.location.hostname : "",
        cpu_info: "",
        token: "",
      })
      load()
    } catch (err) {
      alert("Failed to register worker")
    } finally {
      setRegistering(false)
    }
  }

  if (loading) return <div className="text-muted text-sm">Loading workers&hellip;</div>

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Workers</h1>
          <p className="text-muted text-sm">
            Distributed simulation workers
          </p>
        </div>
        <button
          onClick={() => setRegistering(!registering)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-black rounded-sm text-sm font-medium shadow-glow transition"
        >
          <Plus size={16} /> Register Worker
        </button>
      </div>

      {registering && (
        <Panel padded={false}>
          <div className="p-4">
            <form onSubmit={handleRegister} className="space-y-4 max-w-md">
              <div>
                <label className="block text-[0.625rem] font-medium text-dim mb-1">Worker Name</label>
                <input
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan-500 focus:outline-none"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. bekir-laptop"
                />
              </div>
              <div>
                <label className="block text-[0.625rem] font-medium text-dim mb-1">Hostname</label>
                <input
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan-500 focus:outline-none"
                  value={form.hostname}
                  onChange={(e) => setForm({ ...form, hostname: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[0.625rem] font-medium text-dim mb-1">CPU Info</label>
                <input
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan-500 focus:outline-none"
                  value={form.cpu_info}
                  onChange={(e) => setForm({ ...form, cpu_info: e.target.value })}
                  placeholder="e.g. ARM64"
                />
              </div>
              <div>
                <label className="block text-[0.625rem] font-medium text-dim mb-1">Worker Token</label>
                <input
                  className="w-full bg-surface border border-border rounded-sm px-3 py-2 text-sm text-ink mono focus:border-cyan-500 focus:outline-none"
                  value={form.token}
                  onChange={(e) => setForm({ ...form, token: e.target.value })}
                  placeholder="worker-change-me"
                />
                <p className="text-[0.625rem] text-dim mt-1">Set WORKER_TOKEN in backend env to match</p>
              </div>
              <button
                type="submit"
                disabled={registering || !form.name || !form.token}
                className="w-full py-2 bg-cyan-500 hover:bg-cyan-400 text-black rounded-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-glow transition"
              >
                {registering ? "Registering&hellip;" : "Register Worker"}
              </button>
            </form>
          </div>
        </Panel>
      )}

      <Panel title="Active Workers" padded={false}>
        <div className="p-3">
          {workers.length === 0 ? (
            <p className="text-muted text-sm">No workers registered. Register a worker to run simulations.</p>
          ) : (
            <div className="space-y-2">
              {workers.map((w) => (
                <div key={w.id} className="flex items-center justify-between p-3 rounded-sm border border-border">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2.5 h-2.5 rounded-full ${w.status === "online" ? "bg-ok-500 animate-pulse-slow" : "bg-dim"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink">{w.name}</div>
                      <div className="text-[0.625rem] text-dim">{w.hostname}</div>
                      <div className="text-[0.625rem] text-dim font-mono">{w.cpu_info || "unknown CPU"}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium">
                      {w.status === "online" ? "Online" : w.status === "busy" ? "Busy" : "Offline"}
                    </div>
                    <div className="text-[0.625rem] text-dim font-mono truncate max-w-[120px]" title={w.id}>
                      {w.id.slice(0, 8)}&hellip;
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <StatGrid cols={3}>
        <Stat label="Total workers" value={workers.length.toLocaleString()} status="nominal" accent="blue" />
        <Stat label="Online" value={workers.filter((w) => w.status === "online").length.toLocaleString()} status="safe" accent="cyan" />
        <Stat label="Offline" value={workers.filter((w) => w.status !== "online").length.toLocaleString()} status="muted" accent="amber" />
      </StatGrid>
    </div>
  )

}
